import React from "react";
import { useNavigate } from "react-router-dom";
import { Squares2X2Icon } from "@heroicons/react/24/outline";
import { cn } from "@/lib/utils";
import { CatalogDirectoryProduct } from "../../api/fetchProductCatalogDirectory";
import { landingContentBySlug } from "../../config/landing";

interface CatalogProductCardProps {
  product: CatalogDirectoryProduct;
}

/**
 * Mirrors the Product Store's ProductCard (grid tile with aspect-video
 * thumbnail and p-4 body) minus the commerce bits — these entries are
 * browse-only, not purchasable.
 */
export default function CatalogProductCard({
  product,
}: CatalogProductCardProps) {
  const navigate = useNavigate();

  const hasLandingPage = Boolean(
    product.landingPageKey && landingContentBySlug[product.landingPageKey],
  );

  const handleClick = () => {
    if (!hasLandingPage) return;
    navigate(`/catalog/${product.slug}`);
  };

  const interactiveProps = hasLandingPage
    ? {
        onClick: handleClick,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        },
        role: "button",
        tabIndex: 0,
      }
    : {};

  return (
    <div
      className={cn(
        "flex flex-col group rounded-lg transition-all bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700",
        hasLandingPage
          ? "cursor-pointer hover:border-gray-300 dark:hover:border-slate-600"
          : "cursor-default",
      )}
      {...interactiveProps}
    >
      <div className="relative aspect-video rounded-t-lg overflow-hidden bg-gray-50 dark:bg-slate-900">
        {product.thumbnailUrl ? (
          <img
            alt={product.name}
            className="object-contain w-full h-full"
            src={product.thumbnailUrl}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#17064A] via-[#4E1240] to-[#B20030]">
            <Squares2X2Icon className="h-10 w-10 text-white/70" />
          </div>
        )}
        {product.badge && (
          <div className="absolute bottom-2 right-2 z-10">
            <span className="inline-flex items-center rounded-full bg-white/20 px-2 py-1 text-xs font-medium text-white backdrop-blur-sm">
              {product.badge}
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-semibold text-base mb-1 text-gray-900 dark:text-white">
          {product.name}
        </h3>

        <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
          {product.tagline}
        </p>

        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {product.audiences.map((audience) => (
            <span
              className="rounded bg-gray-100 dark:bg-slate-700 px-1.5 py-0.5 text-xs text-gray-600 dark:text-gray-300"
              key={audience}
            >
              {audience}
            </span>
          ))}
        </div>

        <div className="mt-auto pt-4 flex items-center justify-between gap-4 text-sm text-gray-500 dark:text-gray-400">
          <span className="truncate">{product.trades.join(" · ")}</span>
          {hasLandingPage && (
            <span className="shrink-0 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
              Learn more
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
